/**
 * What the wizard sends, and what counts as "unset".
 *
 * Separate from `alertSpec` because the two answer different questions.
 * `alertSpec` builds the comparison document the engine reads; this builds the
 * request bodies around it — guard policy, recipients, schedule — and those
 * follow the API's conventions rather than the engine's.
 *
 * **The reason this is one module and not inline at each call site.** The Dry
 * run button and the live-count hook each built the dry-run body themselves
 * and disagreed about unset: one treated only `''` as unset, so a null cap
 * became `Number(null) === 0` — a value the endpoint bounds out — and an
 * undefined one became `NaN`, which `JSON.stringify` writes as an explicit
 * null the endpoint reads differently from an absent key.
 */

import {
  COMPARATORS,
  COMPARISON_SHAPES,
  PERIOD_UNITS,
  SEVERITIES,
  buildAlertSpec,
  percentToShare,
} from './alertSpec';
import { defaultScheduleZone } from '../shared/ScheduleZoneField';

/**
 * Every reason the volume guard inputs are not usable, in the author's words.
 *
 * Separate from `validateAlertSpecInputs` because the guard is not part of the
 * comparison spec — it is delivery policy, and the engine's own split keeps
 * the two apart.
 *
 * **These exist because silently dropping a guard is the worst outcome here.**
 * `percentToShare` returns null outside `(0, 100]`, and a null was previously
 * omitted from the payload — so an author who typed `150` got an alert with no
 * share guard at all, created successfully, with nothing said. An alert that
 * looks guarded and is not is the exact failure the guard exists to prevent.
 *
 * A number input's `max`/`min` does not stop a typed value, so neither
 * attribute can be relied on for this.
 */
export const validateGuardInputs = (formData) => {
  const alert = formData.alert ?? {};
  const problems = [];

  const rawMatches = alert.volumeGuardMaxMatches;
  if (String(rawMatches ?? '').trim() !== '') {
    const matches = Number(String(rawMatches).trim());
    if (Number.isNaN(matches) || !Number.isFinite(matches)) {
      problems.push('Maximum matched subjects must be a whole number.');
    } else if (!Number.isSafeInteger(matches)) {
      // `isInteger` admits 1e21, which is integral but not exactly
      // representable — and an integer column could not hold it anyway.
      problems.push('Maximum matched subjects must be a whole number, not a fraction.');
    } else if (matches > 2147483647) {
      // The column is an integer; beyond int4 the write fails server-side.
      problems.push('Maximum matched subjects is too large. Use 2,147,483,647 or less.');
    } else if (matches < 1) {
      // 0 would trip on every run, which is not what an explicit cap means.
      problems.push('Maximum matched subjects must be at least 1. Leave it empty to use the default.');
    }
  }

  const rawShare = alert.volumeGuardMaxShare;
  if (String(rawShare ?? '').trim() !== '') {
    const share = Number(String(rawShare).trim());
    if (Number.isNaN(share) || !Number.isFinite(share)) {
      problems.push('Maximum share of population must be a number.');
    } else if (share <= 0 || share > 100) {
      problems.push('Maximum share of population must be greater than 0 and at most 100 percent.');
    } else if (percentToShare(rawShare) == null) {
      // Inside the stated range but below what the Numeric(6,5) column can
      // hold, so `percentToShare` rounds it to 0 and returns null. Left
      // unreported, create omitted the key and **update sent an explicit null
      // — wiping a share the alert already had**, with a success toast. The
      // "looks guarded and is not" failure these validators exist to prevent.
      problems.push('Maximum share of population is too small to store. Use at least 0.001 percent.');
    }
  }

  return problems;
};

/**
 * The dry-run request body, built once so every caller agrees on what "unset"
 * means.
 *
 * Previously the wizard's Dry run button and the live-count hook each built
 * this inline and disagreed: one treated only `''` as unset, so a null cap
 * became `Number(null) === 0` — a value the endpoint bounds out — and an
 * undefined one became `NaN`, which `JSON.stringify` writes as an explicit
 * null the endpoint reads differently from an absent key.
 */
export const buildDryRunParams = (formData) => {
  const alert = formData.alert ?? {};
  const matches = alert.volumeGuardMaxMatches;
  // No spec means no request. Two cases beyond an incomplete comparison:
  //
  //  - **No dataset.** `datasetIds` has `min_length=1`, so an empty list is
  //    refused — and the live count would render that refusal as an error
  //    banner while the author is still filling the form.
  //  - **An invalid guard.** `percentToShare` returns null outside (0, 100],
  //    and a null is omitted, so the run would report a verdict computed with
  //    no share rule while the field beside it shows an error. An alert that
  //    looks guarded and is not is the failure the guard exists to prevent.
  const unusable = (formData.reportDatasetIds ?? []).length === 0
    || validateGuardInputs(formData).length > 0;
  return {
    alertSpec: unusable ? null : asStoredList(buildAlertSpec(formData)),
    // The query the spec runs, by reference (PRD-1438).
    draftId: alert.queryDraftId ?? undefined,
    // Only in edit mode. Lets the server fall back to this alert's stored query
    // for a comparison the body carries none for.
    automationId: formData.automationId ?? undefined,
    datasetIds: formData.reportDatasetIds ?? [],
    channels: (alert.channels ?? []).length > 0 ? alert.channels : undefined,
    name: formData.automationName || undefined,
    timezone: formData.timezone || undefined,
    volumeGuardMaxMatches: matches === '' || matches == null ? undefined : Number(matches),
    volumeGuardMaxShare: percentToShare(alert.volumeGuardMaxShare) ?? undefined,
  };
};

/**
 * The create body for a new alert.
 *
 * camelCase throughout, because that is what `AutomationCreate` takes.
 * `AutomationUpdate` uses snake_case for the same fields; each model follows
 * its own existing convention and that is not a difference to normalise here.
 *
 * Returns null when the spec is not yet buildable, so a caller cannot send a
 * body the API would refuse.
 */
export const buildCreatePayload = (formData) => {
  const spec = buildAlertSpec(formData);
  if (!spec) return null;

  const alert = formData.alert ?? {};
  const guardMatches = alert.volumeGuardMaxMatches;
  const share = percentToShare(alert.volumeGuardMaxShare);

  const payload = {
    type: 'alerts',
    // Required by AutomationCreate even though an alert creates no dataset;
    // the Reports flow sends the same value for the same reason. Omitting it
    // is a 422, not a default.
    datasetAction: 'update',
    automationName: formData.automationName,
    queryName: formData.queryName || formData.reportQuery || '',
    description: formData.description || null,
    timezone: formData.timezone || defaultScheduleZone(),
    trigger: {
      scheduleType: 'scheduled',
      frequency: formData.trigger?.frequency || 'daily',
      time: formData.trigger?.time || '08:00',
      dayOfWeek: formData.trigger?.dayOfWeek || 'monday',
    },
    reportEmailToSelf: formData.reportEmailToSelf !== false,
    reportRecipients: formData.reportRecipients ?? [],
    // Persisted, not merely dry-run against: `_blocking_reasons` refuses to
    // call an alert ready without them ("no datasets are configured, so there
    // is nothing to evaluate over"), and a scheduled run reads them from the
    // row. Reused from the Report columns, like the recipients.
    reportDatasetIds: formData.reportDatasetIds ?? [],
    alertSpec: asStoredList(spec),
    alertQueryDraftId: alert.queryDraftId ?? undefined,
    // Required on create: omitting it is refused, not treated as empty.
    alertChannels: alert.channels ?? [],
    alertSeverity: alert.severity ?? 'warning',
  };

  // There is deliberately no alertActive. An alert is created inactive and
  // activation is a separate action with its own audit entry, so a create body
  // carrying the field could not switch an alert on even by accident.

  // Omitted rather than sent as null, so an unset cap stays unset and the
  // evaluation default keeps applying.
  if (guardMatches !== '' && guardMatches != null) {
    payload.alertVolumeGuardMaxMatches = Number(guardMatches);
  }
  if (share != null) payload.alertVolumeGuardMaxShare = share;

  return payload;
};

/**
 * `schedule_datetime` is a JSON blob, not a time.
 *
 * The create route stores `json.dumps({frequency, time, dayOfWeek})`
 * (`automation_routes.py`) and the scheduler `json.loads` it
 * (`scheduler_service.py`). Reading it as a bare time put a JSON string into
 * `<input type="time">` — which renders blank — and hard-coding the frequency
 * meant a weekly alert always showed as daily/Monday and neither could be
 * edited.
 *
 * Writing a bare time back was worse: `add_automation_job` throws on
 * `json.loads`, and that throw is caught and logged **after** the commit — so
 * the author got a 200 and an "Alert updated" toast while APScheduler kept the
 * old trigger, and on restart the job could not be registered at all.
 *
 * A legacy bare string is tolerated on read, because rows written before the
 * blob existed still have one.
 */
const scheduleFromStored = (raw) => {
  const fallback = { frequency: 'daily', time: '08:00', dayOfWeek: 'monday' };
  if (raw == null || raw === '') return fallback;
  if (typeof raw === 'object') return { ...fallback, ...raw };
  const text = String(raw).trim();
  if (!text.startsWith('{')) {
    // Legacy: a bare "HH:MM" from before the blob.
    return { ...fallback, time: text };
  }
  try {
    const parsed = JSON.parse(text);
    return {
      frequency: parsed.frequency || fallback.frequency,
      time: parsed.time || fallback.time,
      dayOfWeek: parsed.dayOfWeek || fallback.dayOfWeek,
    };
  } catch {
    // Unparseable: keep the shape rather than putting a blob in a time input.
    return fallback;
  }
};

const scheduleToStored = (trigger) => JSON.stringify({
  frequency: trigger?.frequency || 'daily',
  time: trigger?.time || '08:00',
  dayOfWeek: trigger?.dayOfWeek || 'monday',
});

/** The inverse of `percentToShare`, for populating an edit form. */
export const shareToPercent = (share) => {
  const n = share === '' || share == null ? null : Number(share);
  if (n == null || Number.isNaN(n)) return '';
  // Five decimal places on the stored fraction is two on the percentage.
  return String(Math.round(n * 100 * 100000) / 100000);
};

/**
 * The column's shape: a list, always.
 *
 * **The two endpoints disagreed about this and the wizard was on the wrong side
 * of the disagreement.** `validate_spec` accepts a bare object as a kindness —
 * "a wizard collecting one comparison should not have to know the column is a
 * list" — so create worked. The dry run reads through `alerts_from_json`, which
 * opens with `if not isinstance(raw, list)`, so the live match count answered
 * 422 and the author saw the raw sentence "stored comparisons must be a list,
 * got dict" under the comparison controls.
 *
 * Found in review of #177, by driving the real wizard against a real stack. My
 * own cross-repo check missed it because it fed `buildAlertSpec` output to the
 * *write gate*, which is the lenient one, and the smoke's API save happened to
 * send a list already — so both halves of the proof looked at the side that
 * worked.
 *
 * Wrapping here rather than asking the dry run to loosen is the right direction:
 * a list is what the column actually holds, it is what `validate_spec`
 * normalises to on the way in, and sending the stored shape means the dry run
 * evaluates exactly the document that will be saved rather than something the
 * API happens to accept.
 */
const asStoredList = (spec) => (spec == null ? spec : [spec]);

/**
 * Which stored shape this is.
 *
 * Discriminated on key presence rather than on `version`, for the reason
 * `stored.py` gives: every shape carries a `version` and they all count from 1,
 * so the numbers describe different things and comparing them would read a
 * version 1 spec as a version 1 alert. `sql` picks out version 3, `ir` version
 * 2, neither means version 1.
 */
// Keyed on the key being *present*, not on its value being usable: a document
// carrying `sql: ''` is a broken version 3, not an alert from before the
// migration, and telling its owner it "was created before alerts moved to a
// query plus a comparison" is a sentence about something that did not happen.
// `hasQuery` below is what judges the value. Raised in review of this branch.
const isVersionThree = (spec) => !!spec && typeof spec === 'object' && 'sql' in spec;
const isVersionTwo = (spec) => !!spec && typeof spec === 'object' && !!spec.ir;

/**
 * Whether this wizard can represent the document at all.
 *
 * **Version 2 stays editable, and that is a deliberate choice rather than an
 * oversight (PRD-1405).** A version 2 alert holds an IR and no SQL, so it cannot
 * be saved from this form without being rebuilt — `validateAlertSpecInputs`
 * blocks on the missing query and says so. That costs the author one button
 * press, and the question is stored beside the alert so they are not asked to
 * remember it.
 *
 * The alternative was converting the IR to SQL here, which would have the
 * console author a query nobody previewed — the thing every other rule in this
 * epic exists to prevent. Refusing outright was the other, and it would lock
 * every alert authored in the last release out of its own edit form over a
 * change the author did not make.
 */
const isEditableShape = (spec) => isVersionThree(spec) || isVersionTwo(spec);

/**
 * Why a stored alert cannot be opened in this form.
 *
 * **Refuse rather than repair**, which is the same rule `spec_from_json`
 * applies to a spec it cannot read. Loading a spec this form cannot represent
 * and then saving it would drop whatever the form has no field for — a
 * relation, a run length, a second comparison — and the alert would break
 * silently on its next run. An alert nobody can edit is recoverable; one that
 * edits itself into nonsense is not.
 *
 * @returns {string[]} empty when the alert is safe to edit
 */
export const editBlockers = (automation) => {
  const raw = automation?.alert_spec;
  const specs = Array.isArray(raw) ? raw : raw ? [raw] : [];

  if (specs.length === 0) {
    return ['This alert has no stored comparison, so there is nothing to edit here.'];
  }
  if (specs.length > 1) {
    // One alert may carry several comparisons; this form authors exactly one.
    return [`This alert runs ${specs.length} comparisons. The wizard edits one, so saving would drop the rest.`];
  }

  const spec = specs[0];

  // **A version 1 alert is not editable here, and converting it silently would
  // be worse than saying so.**
  //
  // Every alert saved before PRD-1320 carries a `ComparisonSpec` — a comparison
  // that names physical tables and columns and has no query beside it. This
  // wizard authors a query plus a comparison over its result, and the question
  // that would produce that query is not recoverable from the stored spec: the
  // spec is the *output* of an authoring sentence nobody kept.
  //
  // The platform's answer to a version 1 document is the mechanical transform in
  // `migrate.py`, which runs on read, server-side, and is deliberately not a
  // data rewrite. There is no endpoint that hands the resolved result back, so
  // the wizard cannot show an author what their alert became — and a form that
  // opened blank and saved would replace a working comparison with one built
  // from whatever the author typed to get past the empty step.
  //
  // So: refuse, in the same spirit as the rest of this function. An alert nobody
  // can edit is recoverable; one that edits itself into nonsense is not.
  if (!isEditableShape(spec)) {
    return [
      'This alert was created before alerts moved to a query plus a comparison, '
      + 'so this wizard cannot edit it. It keeps running as configured. To change '
      + 'it, create a new alert describing what to watch and switch the old one off.',
    ];
  }

  const comparison = spec.comparison;
  if (!comparison || typeof comparison !== 'object') {
    return ['This alert has no stored comparison, so there is nothing to edit here.'];
  }
  // Each shape holds its query under its own key and in its own type, so the
  // check is per shape rather than on one field. A document carrying neither has
  // already been refused above as version 1.
  const hasQuery = isVersionThree(spec)
    ? typeof spec.sql === 'string' && spec.sql.trim() !== ''
    : !!spec.ir && typeof spec.ir === 'object';
  if (!hasQuery) {
    return ['This alert has no stored query, so there is nothing for the comparison to read.'];
  }

  // **A version 3 document whose query moves but has nothing to move it.**
  //
  // The window binds `%(window_start)s` / `%(window_end)s` at run time. A stored
  // query carrying those placeholders with no `window` beside it fails on every
  // evaluation, and the API refuses that pairing on the way in — so a row in
  // that state was hand-edited or predates the refusal. Opening it here and
  // saving would write the same broken pairing back under a success toast.
  if (isVersionThree(spec) && /%\(window_(start|end)\)s/.test(spec.sql) && !spec.window) {
    return [
      'This alert stores a moving time window with nothing to bind it to, so it '
      + 'cannot run as saved. Rebuild the question to repair it.',
    ];
  }

  const shape = COMPARISON_SHAPES.find((s) => s.id === comparison.shape);
  if (!shape) {
    return [`This alert uses the "${comparison.shape}" comparison, which this wizard cannot represent yet.`];
  }

  const blockers = [];

  // A value outside the form's own vocabulary would render as a blank control
  // and then be written back as whatever the blank resolves to — silently
  // changing the rule. `Comparator` and `PeriodUnit` each have exactly the
  // members offered today, so this is a guard against the enums growing rather
  // than a live defect; the point is that growth should refuse, not corrupt.
  if (comparison.comparator && !COMPARATORS.some((c) => c.id === comparison.comparator)) {
    blockers.push(`It uses the comparator "${comparison.comparator}", which this wizard does not offer.`);
  }
  if (comparison.period_unit && !PERIOD_UNITS.some((u) => u.id === comparison.period_unit)) {
    blockers.push(`It counts periods in "${comparison.period_unit}", which this wizard does not offer.`);
  }
  if (automation?.alert_severity
      && !SEVERITIES.some((sev) => sev.id === automation.alert_severity)) {
    blockers.push(`It uses the severity "${automation.alert_severity}", which this wizard does not offer.`);
  }

  // **Any stored field the chosen shape does not read would be lost on save.**
  //
  // This is stricter than it looks, and deliberately so. The API refuses a field
  // the shape does not read rather than ignoring it, so a document carrying one
  // could not have been written by this wizard — it came from a migration or a
  // hand edit. Opening it, dropping the field and saving would produce a
  // *different* alert under the same name, which is the silent change the whole
  // epic exists to remove.
  const carriesButCannotShow = [
    ['measure_column', 'measureColumn', 'the column it measures'],
    ['period_column', 'periodColumn', 'the column it groups periods by'],
    ['period_unit', 'periodUnit', 'how long one period is'],
    ['run_length', 'runLength', 'a run length'],
  ].filter(([key, control]) => comparison[key] != null && !shape.uses[control]);
  carriesButCannotShow.forEach(([, , what]) => {
    blockers.push(`It stores ${what}, which a "${shape.label}" comparison has no field for.`);
  });

  // `round_to` is the parity shim PRD-1320 sets on migrated alerts, and this
  // wizard deliberately never writes one — so a stored one would be dropped on
  // save, and dropping it changes what the alert matches. That is precisely the
  // case the shim exists to prevent, so it blocks rather than silently
  // un-migrating the alert.
  if (comparison.round_to != null) {
    blockers.push(
      'It rounds its measure before comparing, which this wizard cannot set. '
      + 'Saving would change which subjects match.',
    );
  }

  // Both or neither is what the API refuses; the form models the threshold as a
  // choice, so a document carrying both could not be represented at all.
  if (comparison.threshold != null && comparison.threshold_column != null) {
    blockers.push('It stores both a fixed threshold and a threshold column, which this wizard cannot represent.');
  }

  return blockers;
};

/**
 * Wizard form state from a stored alert (`AutomationResponse`).
 *
 * **The guard cap is the trap this exists to get right.** The read-back returns
 * the *stored* value, not the effective one: a null cap means the author never
 * set one and a default applies at evaluation. So null maps to an empty input,
 * never to the default — writing the default in would persist an inherited
 * value as an explicit choice on the next save, and the alert would stop
 * tracking the default if it ever changed.
 *
 * **The result columns are stored from version 3 on, and are seeded from the
 * document rather than invented.** Version 2 held only the query and the
 * comparison, so an edit form opened with the chosen columns in place and an
 * empty option list until the preview re-ran. Version 3 stores the columns the
 * query was seen to return — they are what the comparison is checked against —
 * so the controls offer the real options on open.
 *
 * `validateAlertSpecInputs` is still written for the empty case, because a
 * version 2 alert still opens that way: a chosen column is only reported stale
 * once there is a result to check it against, so opening an alert does not
 * accuse it of naming columns that do not exist.
 */
export const formDataFromAutomation = (automation) => {
  const raw = automation?.alert_spec;
  const spec = (Array.isArray(raw) ? raw[0] : raw) ?? {};
  const comparison = (isEditableShape(spec) ? spec.comparison : null) ?? {};

  return {
    type: 'alerts',
    // **Carried so the dry run can find the stored query** (PRD-1438). An edit
    // that did not regenerate sends neither the query nor a draft reference, and
    // the server restores it from this alert. Without it a document with no
    // `sql` key is read as version 1 and refused as "unknown spec key(s)".
    automationId: automation?.id ?? null,
    automationName: automation?.name || '',
    description: automation?.description || '',
    // What the alert does now, in the Activity Log's own words (PRD-1382). The
    // server derives it from the stored spec; rendering our own sentence from
    // the same document would be a second renderer free to drift from the
    // record, and a conversation that describes an alert differently from its
    // own log is the hardest disagreement to notice.
    alertSummary: Array.isArray(automation?.alert_summary) ? automation.alert_summary : [],
    queryName: automation?.query_name || '',
    reportQuery: automation?.query_name || '',
    reportEmailToSelf: automation?.report_email_to_self !== false,
    reportRecipients: Array.isArray(automation?.report_recipients)
      ? automation.report_recipients
      : [],
    reportDatasetIds: Array.isArray(automation?.report_dataset_ids)
      ? automation.report_dataset_ids.map(String)
      : [],
    timezone: automation?.timezone || defaultScheduleZone(),
    trigger: {
      scheduleType: automation?.schedule_type || 'scheduled',
      ...scheduleFromStored(automation?.schedule_datetime),
    },
    alert: {
      // Kept so a save does not rename the stored comparison.
      alertId: comparison.alert_id ?? '',
      // The query, carried whole. The wizard does not read into it — it is the
      // authoring endpoint's output and the evaluator's input, and a console
      // that edited it by hand would be authoring SQL by another name.
      //
      // Null for a version 2 alert, which is what asks the author for one
      // rebuild before it will save. See `isEditableShape`.
      sql: isVersionThree(spec) ? spec.sql : null,
      window: isVersionThree(spec) ? (spec.window ?? null) : null,
      // **Stored now, so seeded rather than left empty.** Under version 2 the
      // columns the query returned were not part of the document, so this opened
      // blank and the option lists filled in only once the author re-ran the
      // preview. Version 3 stores them — they are what the comparison is checked
      // against — so the controls can offer the real options immediately, and a
      // save that does not rebuild writes back the columns the alert was
      // authored with rather than an empty list.
      //
      // Names only: the stored document keeps what the comparison needs, not the
      // descriptions the picker shows. Those come back with the next preview.
      resultColumns: Array.isArray(spec.result_columns)
        ? spec.result_columns.map((name) => ({ name, description: null }))
        : [],
      authorRefusal: null,
      shape: comparison.shape ?? 'threshold',
      subjectColumn: comparison.subject_column ?? '',
      measureColumn: comparison.measure_column ?? '',
      comparator: comparison.comparator ?? '>=',
      // Exactly one of the two is stored, and which one decides the control the
      // form shows. Defaulting to the fixed number when neither is present is
      // safe because that is also the create default, so a half-written
      // document opens as a blank threshold rather than a blank column picker.
      thresholdKind: comparison.threshold_column != null ? 'column' : 'value',
      threshold: comparison.threshold != null ? String(comparison.threshold) : '',
      thresholdColumn: comparison.threshold_column ?? '',
      periodColumn: comparison.period_column ?? '',
      periodUnit: comparison.period_unit ?? '',
      runLength: comparison.run_length != null ? String(comparison.run_length) : '',
      channels: Array.isArray(automation?.alert_channels) && automation.alert_channels.length > 0
        ? automation.alert_channels
        : ['email'],
      severity: automation?.alert_severity ?? 'warning',
      // Null means "never set" — an empty input, not the default.
      volumeGuardMaxMatches: automation?.alert_volume_guard_max_matches != null
        ? String(automation.alert_volume_guard_max_matches)
        : '',
      volumeGuardMaxShare: shareToPercent(automation?.alert_volume_guard_max_share),
    },
  };
};

/**
 * The PATCH body for an existing alert.
 *
 * **snake_case, unlike create.** `AutomationUpdate` follows its own existing
 * convention and `AutomationCreate` follows a different one; that difference is
 * not a mistake for the client to normalise away.
 *
 * No `alert_active` here either: activation is a separate action with its own
 * audit entry, so an edit form cannot switch an alert on even by accident.
 *
 * A guard the author cleared is sent as an explicit null rather than omitted —
 * omitting it would leave the old cap in place, which is the opposite of what
 * clearing the field means. That is the one place null is the right value.
 */
export const buildUpdatePayload = (formData) => {
  const spec = buildAlertSpec(formData);
  if (!spec) return null;
  const alert = formData.alert ?? {};
  const trigger = formData.trigger ?? {};
  const matches = alert.volumeGuardMaxMatches;
  const share = percentToShare(alert.volumeGuardMaxShare);

  const payload = {
    name: (formData.automationName || '').trim(),
    description: (formData.description || '').trim() || null,
    query_name: (formData.queryName || formData.reportQuery || '').trim() || null,
    schedule_type: trigger.scheduleType || 'scheduled',
    schedule_datetime: scheduleToStored(trigger),
    timezone: formData.timezone || defaultScheduleZone(),
    report_email_to_self: formData.reportEmailToSelf !== false,
    report_recipients: formData.reportRecipients ?? [],
    report_dataset_ids: formData.reportDatasetIds ?? [],
    alert_spec: asStoredList(spec),
    // Only when the query was regenerated. An edit that merely moves the
    // threshold sends neither this nor a query, and the server keeps the stored
    // one — which is what makes the common edit carry no SQL at all.
    alert_query_draft_id: alert.queryDraftId ?? undefined,
    alert_channels: alert.channels ?? [],
    alert_severity: alert.severity ?? 'warning',
  };

  // **A null does not clear a guard.** `authoring.alert_columns` writes a
  // column only when the value is not None — its docstring says an absent
  // field means "leave it alone", never "clear it" — and `validate_max_share`
  // / `validate_positive_int` both return None for None. So sending null
  // saved a 200 and a success toast while the old cap stayed, and reopening
  // showed it again.
  //
  // Omitted rather than sent, since sending it does nothing, and the wizard
  // blocks the save with an explanation instead of reporting a false success.
  if (matches !== '' && matches != null) {
    payload.alert_volume_guard_max_matches = Number(matches);
  }
  if (share != null) payload.alert_volume_guard_max_share = share;

  return payload;
};

/**
 * Guard fields the author has cleared on an edit, which the API cannot honour.
 *
 * Returned so the wizard can block with a reason rather than save and lie.
 * Needs an API change to fix properly — a sentinel, or `model_fields_set`
 * handling on the update model.
 */
export const clearedGuardFields = (formData, stored) => {
  if (!stored) return [];
  const alert = formData.alert ?? {};
  const out = [];
  const wasSet = (v) => v != null;
  const nowBlank = (v) => String(v ?? '').trim() === '';
  if (wasSet(stored.alert_volume_guard_max_matches) && nowBlank(alert.volumeGuardMaxMatches)) {
    out.push('Clearing the maximum matched subjects is not supported yet — the API treats an empty value as “leave unchanged”. Set a number, or leave the previous one in place.');
  }
  if (wasSet(stored.alert_volume_guard_max_share) && nowBlank(alert.volumeGuardMaxShare)) {
    out.push('Clearing the maximum share is not supported yet — the API treats an empty value as “leave unchanged”. Set a number, or leave the previous one in place.');
  }
  return out;
};
